import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Container } from "@mui/material";
import { motion } from "framer-motion";
import Homenavbar from "./Homenavbar";
import avatarSarah from "../src/Components/images/avatar_sarah.png";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import { IconButton } from "@mui/material";

const HeroSection = ({ onRequestDemo, activeLink, setActiveLink }) => {
  const navigate = useNavigate();
  return (
    <Box
      id="home-section"
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "linear-gradient(15deg, #FFFFFF 0%, #F1F5F9 100%)", // Light Premium Gradient
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        {/* Grid Pattern Overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.4,
            backgroundImage: "radial-gradient(#CBD5E1 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />

        {/* Floating Particles - Framer Motion */}
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -50, 0],
              x: [0, i % 2 === 0 ? 30 : -30, 0],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 5 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.3,
            }}
            style={{
              position: "absolute",
              top: `${10 + (i * 8) % 80}%`,
              left: `${5 + (i * 9) % 90}%`,
              width: `${Math.random() * 60 + 20}px`,
              height: `${Math.random() * 60 + 20}px`,
              borderRadius: "50%",
              background:
                i % 3 === 0
                  ? "radial-gradient(circle, rgba(255, 107, 0, 0.2) 0%, transparent 70%)" // Light Orange
                  : i % 3 === 1
                    ? "radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%)" // Light Blue
                    : "radial-gradient(circle, rgba(249, 115, 22, 0.2) 0%, transparent 70%)", // Light Orange
              filter: "blur(8px)",
            }}
          />
        ))}
      </Box>
      {/* Navbar */}
      <Homenavbar
        onRequestDemo={onRequestDemo}
        activeLink={activeLink}
        setActiveLink={setActiveLink}
      />

      {/* Hero Content */}
      <Container
        maxWidth="lg"
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          pt: { xs: 12, md: 15 },
          pb: 8,
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 4, md: 10 },
            alignItems: "center",
            width: "100%",
            px: { md: 2 },
          }}
        >
          {/* Left Column - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
              {/* Badge */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: "rgba(255, 107, 0, 0.1)",
                  border: "1px solid rgba(255, 107, 0, 0.2)",
                  borderRadius: "50px",
                  padding: "8px 20px",
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "#FF6B00",
                    boxShadow: "0 0 10px rgba(255, 107, 0, 0.5)",
                  }}
                />
                <Typography
                  sx={{
                    color: "#FF6B00",
                    fontSize: "14px",
                    fontWeight: 500,
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  Connecting Talent with Opportunity
                </Typography>
              </Box>

              {/* Main Heading */}
              <Typography
                variant="h1"
                sx={{
                  color: "#0B1121",
                  fontWeight: 700,
                  fontSize: { xs: "32px", md: "42px", lg: "52px" },
                  lineHeight: 1.1,
                  mb: 2,
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                The Smarter Way to
                <br />
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg, #FF9100 0%, #FF6B00 100%)",
                    backgroundClip: "text",
                    textFillColor: "transparent",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Bench Sales
                </Box>
              </Typography>

              {/* Subtext */}
              <Typography
                sx={{
                  color: "#475569",
                  fontSize: { xs: "15px", md: "16px" },
                  maxWidth: { xs: "100%", md: "480px" },
                  mx: { xs: "auto", md: 0 },
                  mb: 4,
                  lineHeight: 1.6,
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                Connect recruiters with top IT talent instantly. Whether you're
                placing consultants or finding your next opportunity, Benchsales
                makes it effortless.
              </Typography>

              {/* Buttons */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  justifyContent: { xs: "center", md: "flex-start" },
                  alignItems: "center",
                }}
              >
                {/* Recruiter Button - Green */}
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/login')}
                  sx={{
                    background:
                      "linear-gradient(135deg, #FF6B00 0%, #E65100 100%)",
                    color: "white",
                    fontSize: "15px",
                    fontWeight: 500,
                    padding: "12px 28px",
                    borderRadius: "8px",
                    textTransform: "none",
                    fontFamily: "'Poppins', sans-serif",
                    minWidth: "180px",
                    boxShadow: "0 4px 14px 0 rgba(255, 107, 0, 0.39)",
                    "&:hover": {
                      boxShadow: "0 6px 20px rgba(255, 107, 0, 0.23)",
                      background:
                        "linear-gradient(135deg, #E65100 0%, #FB8C00 100%)",
                    },
                  }}
                >
                  I'm a Recruiter
                </Button>

                {/* Job Seeker Button - Orange */}
                <Button
                  variant="outlined"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/login')}
                  sx={{
                    borderColor: "#1E293B",
                    color: "#1E293B",
                    fontSize: "15px",
                    fontWeight: 500,
                    padding: "12px 28px",
                    borderRadius: "8px",
                    textTransform: "none",
                    fontFamily: "'Poppins', sans-serif",
                    minWidth: "180px",
                    borderWidth: "2px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderWidth: "2px",
                      borderColor: "#0F172A",
                      background: "rgba(30, 41, 59, 0.05)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  I'm a Job Seeker
                </Button>
              </Box>
            </Box>

            {/* Stats Footer */}
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" },
                gap: { xs: 2, md: 3 },
                flexWrap: { xs: "wrap", md: "nowrap" },
                borderTop: "1px solid #E2E8F0",
                pt: 3,
                width: "100%",
                mt: 3,
              }}
            >
              {[
                { label: "Active Recruiters", value: "10K+" },
                { label: "Consultants", value: "50K+" },
                { label: "Companies", value: "5K+" },
              ].map((stat) => (
                <Box
                  key={stat.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    textAlign: "left",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#FF6B00",
                      fontWeight: 700,
                      fontSize: { xs: "20px", md: "24px" },
                      fontFamily: "'Poppins', sans-serif",
                      lineHeight: 1,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#64748B",
                      fontSize: { xs: "12px", md: "13px" },
                      fontWeight: 500,
                      fontFamily: "'Poppins', sans-serif",
                      textAlign: "left",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </motion.div>

          {/* Right Column - 5 Floating Cards with Connecting Lines */}
          <Box
            sx={{
              position: "relative",
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              height: "550px",
              width: "100%",
            }}
          >
            {/* SVG Connecting Lines */}
            <svg
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 0,
                pointerEvents: "none",
              }}
            >
              {/* Line from Card 1 to Card 2 */}
              <motion.path
                d="M 200 120 Q 250 150, 300 100"
                stroke="rgba(255, 107, 0, 0.3)"
                strokeWidth="2"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
              {/* Line from Card 2 to Card 3 */}
              <motion.path
                d="M 380 100 Q 420 200, 450 280"
                stroke="rgba(255, 107, 0, 0.3)"
                strokeWidth="2"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2, delay: 0.5, repeat: Infinity, ease: "easeInOut" }}
              />
              {/* Line from Card 3 to Card 4 */}
              <motion.path
                d="M 450 300 Q 300 400, 180 450"
                stroke="rgba(255, 107, 0, 0.3)"
                strokeWidth="2"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2, delay: 1, repeat: Infinity, ease: "easeInOut" }}
              />
              {/* Line from Card 4 to Card 5 */}
              <motion.path
                d="M 180 450 Q 250 430, 350 460"
                stroke="rgba(255, 107, 0, 0.3)"
                strokeWidth="2"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2, delay: 1.5, repeat: Infinity, ease: "easeInOut" }}
              />
            </svg>

            {/* Card 1 - Profile Card (Top Left) */}
            <motion.div
              animate={{
                y: [0, -12, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              style={{
                position: "absolute",
                top: "10%",
                left: "10%",
                width: "190px",
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  background: "white",
                  borderRadius: "14px",
                  padding: "16px",
                  boxShadow: "0 15px 50px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #E2E8F0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.2 }}>
                  <Box
                    component="img"
                    src={avatarSarah}
                    alt="Sarah Chen"
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid #FF6B00",
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#0B1121",
                      }}
                    >
                      Sarah Chen
                    </Typography>
                    <Typography sx={{ fontSize: "10px", color: "#64748B" }}>
                      Full Stack Dev
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
                  {["React", "Python"].map((skill) => (
                    <Box
                      key={skill}
                      sx={{
                        fontSize: "9px",
                        padding: "3px 7px",
                        background: "rgba(255, 107, 0, 0.1)",
                        color: "#FF6B00",
                        borderRadius: "4px",
                        fontWeight: 500,
                      }}
                    >
                      {skill}
                    </Box>
                  ))}
                </Box>
              </Box>
            </motion.div>

            {/* Card 2 - Stats Card (Top Right) */}
            <motion.div
              animate={{
                y: [0, -10, 0],
              }}
              transition={{
                duration: 4.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.5,
              }}
              style={{
                position: "absolute",
                top: "8%",
                right: "8%",
                width: "170px",
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "14px",
                  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
                  border: "1px solid #E2E8F0",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#64748B",
                    mb: 0.8,
                  }}
                >
                  Placements
                </Typography>
                <Typography
                  sx={{
                    fontSize: "24px",
                    fontWeight: 700,
                    color: "#FF6B00",
                    mb: 0.5,
                  }}
                >
                  847
                </Typography>
                <Typography sx={{ fontSize: "9px", color: "#FF6B00", fontWeight: 600 }}>
                  ↑ 23% this month
                </Typography>
              </Box>
            </motion.div>

            {/* Card 3 - Notification Card (Middle Right) */}
            <motion.div
              animate={{
                y: [0, -8, 0],
                rotate: [0, 2, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1,
              }}
              style={{
                position: "absolute",
                top: "48%",
                right: "5%",
                width: "180px",
                zIndex: 3,
              }}
            >
              <Box
                sx={{
                  background: "linear-gradient(135deg, #FF6B00 0%, #E65100 100%)",
                  borderRadius: "12px",
                  padding: "12px",
                  boxShadow: "0 12px 35px rgba(255, 107, 0, 0.3)",
                  color: "white",
                }}
              >
                <Typography sx={{ fontSize: "11px", fontWeight: 600, mb: 0.5 }}>
                  🎉 New Match!
                </Typography>
                <Typography sx={{ fontSize: "9px", opacity: 0.95 }}>
                  3 candidates match your requirements
                </Typography>
              </Box>
            </motion.div>

            {/* Card 4 - Skills Card (Bottom Left) */}
            <motion.div
              animate={{
                y: [0, 10, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.3,
              }}
              style={{
                position: "absolute",
                bottom: "15%",
                left: "5%",
                width: "160px",
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "12px",
                  boxShadow: "0 10px 35px rgba(0, 0, 0, 0.08)",
                  border: "1px solid #E2E8F0",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 600,
                    color: "#64748B",
                    mb: 1,
                  }}
                >
                  Top Skills
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.7 }}>
                  {["JavaScript", "AWS", "Docker"].map((skill, idx) => (
                    <Box
                      key={skill}
                      sx={{
                        fontSize: "9px",
                        padding: "4px 8px",
                        background: idx === 0 ? "rgba(255, 107, 0, 0.1)" : "rgba(100, 116, 139, 0.08)",
                        color: idx === 0 ? "#FF6B00" : "#64748B",
                        borderRadius: "5px",
                        fontWeight: 500,
                      }}
                    >
                      {skill}
                    </Box>
                  ))}
                </Box>
              </Box>
            </motion.div>

            {/* Card 5 - Company Logos (Bottom Right) */}
            <motion.div
              animate={{
                y: [0, -7, 0],
              }}
              transition={{
                duration: 4.2,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1.5,
              }}
              style={{
                position: "absolute",
                bottom: "12%",
                right: "15%",
                width: "150px",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  background: "white",
                  borderRadius: "10px",
                  padding: "10px",
                  boxShadow: "0 8px 30px rgba(0, 0, 0, 0.07)",
                  border: "1px solid #E2E8F0",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "9px",
                    fontWeight: 600,
                    color: "#64748B",
                    mb: 0.8,
                  }}
                >
                  Trusted by
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8 }}>
                  {["Google", "Meta", "Amazon", "Apple"].map((company) => (
                    <Box
                      key={company}
                      sx={{
                        fontSize: "8px",
                        padding: "5px",
                        background: "#F8FAFC",
                        color: "#475569",
                        borderRadius: "5px",
                        fontWeight: 600,
                        textAlign: "center",
                      }}
                    >
                      {company}
                    </Box>
                  ))}
                </Box>
              </Box>
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Scroll Down Icon */}
      <Box
        sx={{
          position: "absolute",
          bottom: 30,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={() => {
          document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
        }}
      >
        <motion.div
          animate={{
            y: [0, 10, 0],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <IconButton
            sx={{
              color: "#FF6B00",
              background: "rgba(255, 107, 0, 0.05)",
              border: "1px solid rgba(255, 107, 0, 0.2)",
              "&:hover": {
                background: "rgba(255, 107, 0, 0.1)",
              },
            }}
          >
            <KeyboardDoubleArrowDownIcon />
          </IconButton>
        </motion.div>

      </Box>
    </Box >
  );
};

export default HeroSection;